/**
 * What a bidder is on the hook for, if the sale ended now.
 *
 * 🔴 SHARED BECAUSE IT IS ARITHMETIC ABOUT SOMEBODY'S MONEY. It lived inside
 * the website's `ExposureMeter` while the website was the only surface that
 * had one. The phone got the meter on 2026-09-22, and a second copy of a
 * money sum is exactly what `memory/feedback_never_retype_a_money_function.md`
 * exists to stop — a hand-typed `place_bid` diverged eleven ways.
 *
 * 🔴 EVERY FIGURE IS THE CALLER'S OWN. The ceilings come from their own `bids`
 * rows under `bids_self_select`; no other bidder's maximum is readable on a
 * client, anywhere. Showing a rival's ceiling would hand away the one thing
 * proxy bidding exists to keep secret. This module never sees a bidder id — it
 * takes the numbers already narrowed to one person.
 */

export interface ExposureLot {
  id: string;
  lotNumber: number;
  title: string;
  /** What the lot stands at now, in cents. */
  current: number;
}

export interface ExposureTotals {
  /** The sum of what the winning lots stand at, with premium applied. */
  winningNow: number;
  /** The same lots if every one of this bidder's maximums were reached. */
  maxExposure: number;
  /** Per lot, the figure the detail row shows. Keyed by lot id. */
  perLot: Map<string, number>;
  /** The premium as a percentage string, for the toggle's label: `12`, `12.5`. */
  premiumLabel: string;
}

/**
 * 🔴 A CEILING BELOW THE CURRENT PRICE IS NOT EXPOSURE. The proxy has already
 * stopped bidding there — somebody else has passed this bidder's maximum, so
 * what they stand to pay is the current price, not a ceiling they are no
 * longer competing at. `Math.max` keeps the figure honest either way, and
 * getting it backwards would overstate what a bidder owes.
 */
function lotExposure(lot: ExposureLot, userMax: ReadonlyMap<string, number>): number {
  return Math.max(lot.current, userMax.get(lot.id) ?? 0);
}

/**
 * `allIn` applies the buyer's premium. It is the DEFAULT view on both
 * surfaces, because the hammer price is not what anyone pays — the toggle to
 * hammer-only exists for reconciling against the room, not because it is the
 * more honest number.
 */
export function computeExposure(
  winningLots: readonly ExposureLot[],
  userMax: ReadonlyMap<string, number>,
  premiumBps: number | null,
  allIn: boolean,
): ExposureTotals {
  const bps = premiumBps ?? 0;
  const mult = allIn ? 1 + bps / 10000 : 1;

  const perLot = new Map<string, number>();
  for (const lot of winningLots) {
    perLot.set(lot.id, Math.round(lotExposure(lot, userMax) * mult));
  }

  return {
    winningNow: Math.round(winningLots.reduce((sum, l) => sum + l.current, 0) * mult),
    maxExposure: Math.round(
      winningLots.reduce((sum, l) => sum + lotExposure(l, userMax), 0) * mult,
    ),
    perLot,
    premiumLabel: (bps / 100).toFixed(bps % 100 === 0 ? 0 : 1),
  };
}

/**
 * The caller's own ceilings, from their `bids` rows, highest per lot.
 *
 * A lot can carry several of this bidder's bids; the ceiling that matters is
 * the highest one they are still standing behind. Rejected bids are the
 * caller's to exclude before calling — they are not a commitment.
 */
export function highestOwnMaximums(
  rows: readonly { lot_id: string; max_amount_cents: number | null }[],
): Map<string, number> {
  const max = new Map<string, number>();
  for (const row of rows) {
    if (row.max_amount_cents === null) continue;
    max.set(row.lot_id, Math.max(max.get(row.lot_id) ?? 0, row.max_amount_cents));
  }
  return max;
}
